import { data, init, tag } from '@blotoutio/edgetag-sdk-js'

import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import { TRACK_PAGE_TYPE } from '@src/quince-site-common/src/domains/PageTypes'
import { ANALYTIC_COOKIES, DEFAULT_CURRENCY_CODE, QUINCE_DOMAIN } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getPageContextDetails } from '@src/quince-site-common/src/utils/getPageContextDetails'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import { generateUniqueEventId } from '@src/telemetry/track'
import { getStorage } from '@src/utils/enhancedStorage'
import { getSessionId } from '@src/utils/user/session.browser'

import { TELEMETRY_DOMAINS } from '../../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../../telemetry-events.constants'
import { processImageUrl } from '../provider-utils/data-layer-pixel-utils'
const EVENT_TYPE_PAGE_VIEW = 'PageView'
const EVENT_TYPE_ADD_TO_CART = 'AddToCart'
const EVENT_TYPE_VIEW_CONTENT = 'ViewContent'
const EVENT_TYPE_REMOVE_FROM_CART = 'RemoveFromCart'
const EVENT_TYPE_SEARCH = 'Search'
const EVENT_TYPE_LEAD = 'Lead'
const EVENT_TYPE_COMPLETE_REGISTRATION = 'CompleteRegistration'
const BRAND_QUINCE = 'Quince'
const PRODUCT_TYPE_PRODUCT = 'product'
const PRODUCT_TYPE_PRODUCT_GROUP = 'product_group'

interface EventProperties {
  productId: string
  quantity: number
  price: number
  variantId: string
  sku: string
  productTitle: string
  variantTitle: string
  description: string
  productType: string
  variantImageUrl: string
  slug: string
  department: string
  spid: string | number
  eventId: string
}

interface SearchEventProperties {
  searchQuery: string
  eventId: string
}

export const setUserData = (user: { id?: string; email?: string; firstName?: string; lastName?: string }) => {
  data({
    ...user,
  })
}

export const EdgeTag: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.EDGE_TAG,
    featureGateName: 'ssr_telemetry_edge_tag',
  },

  initialize: () => {
    // Call init() directly from the imported NPM package
    init({ edgeURL: TELEMETRY_DOMAINS.EDGE_TAG, disableConsentCheck: true })

    // Immediately tag the initial PageView
    // This will now run instantly and reliably every time
    tag(EVENT_TYPE_PAGE_VIEW, {
      visitorId: getlbUserId()?.trim() ?? '',
      sessionId: getSessionId(),
      q_cookie_id: getCookie(ANALYTIC_COOKIES.Q_COOKIE_ID) ?? '',
      eventTime: Date.now(),
      quinceEventId: generateUniqueEventId(),
    })
  },

  events: {
    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: properties => {
      logEdgeTagEvent(EVENT_TYPE_ADD_TO_CART, properties)
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW]: properties => {
      // Fire ViewContent event with required fields
      const eventProperties: EventProperties = {
        productId: properties.productId?.toString() ?? '',
        quantity: 1,
        price: typeof properties.price === 'string' ? Number(properties.price) : (properties.price ?? 0),
        variantId: properties.variantId?.toString() ?? '',
        sku: properties.sku ?? '',
        productTitle: properties.productTitle ?? '',
        variantTitle: properties.variantTitle ?? '',
        description: '',
        productType: properties.productType ?? '',
        variantImageUrl: properties.variantImageUrl ?? '',
        slug: properties.slug ?? '',
        department: properties.department ?? '',
        spid: properties.productId ?? '',
        eventId: properties.eventId ?? '',
      }
      logEdgeTagEvent(EVENT_TYPE_VIEW_CONTENT, eventProperties)

      // Note: PageView is handled by initialize() and PAGE_VIEW event handler
      // No need to fire it here to avoid duplicate PageViews
    },

    [TELEMETRY_EVENT_NAMES.REMOVE_FROM_CART]: properties => {
      logEdgeTagEvent(EVENT_TYPE_REMOVE_FROM_CART, properties)
    },

    [TELEMETRY_EVENT_NAMES.SEARCH]: (properties: SearchEventProperties) => {
      // setUserProperties()
      tag(EVENT_TYPE_SEARCH, {
        searchQuery: properties.searchQuery,
        currency: DEFAULT_CURRENCY_CODE,
        eventTime: Date.now(),
        visitorId: getlbUserId()?.trim() ?? '',
        sessionId: getSessionId(),
        q_cookie_id: getCookie(ANALYTIC_COOKIES.Q_COOKIE_ID) ?? '',
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PAGE_VIEW]: properties => {
      tag(EVENT_TYPE_PAGE_VIEW, {
        visitorId: getlbUserId()?.trim() ?? '',
        sessionId: getSessionId(),
        q_cookie_id: getCookie(ANALYTIC_COOKIES.Q_COOKIE_ID) ?? '',
        eventTime: Date.now(),
        quinceEventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.VARIANT_SELECT]: properties => {
      const isSingleVariant = properties.variantIds.length === 1
      if (!properties.is_product_card && properties.productTitle) {
        if (isSingleVariant && properties.variantIds.length > 0 && properties.variantIds[0]) {
          // Fire ViewContent event for single variant selection
          const content = {
            id: properties.variantIds[0],
            quantity: 1,
            item_price: formatRevenue(Number(properties.price)),
            variantId: properties.variantIds[0],
            sku: properties.variantIds[0],
            title: properties.productTitle + (properties.variantTitle ? ` - ${properties.variantTitle}` : ''),
            category: properties.department,
            brand: BRAND_QUINCE,
            type: PRODUCT_TYPE_PRODUCT,
            image: '', // Will be processed by processImageUrl if needed
            url: getProductURL(''), // Will be constructed from product data if needed
          }

          tag(EVENT_TYPE_VIEW_CONTENT, {
            currency: DEFAULT_CURRENCY_CODE,
            value: formatRevenue(Number(properties.price)),
            contents: [content],
            url: typeof window !== 'undefined' ? window.location.href : '',
            visitorId: getlbUserId()?.trim() ?? '',
            sessionId: getSessionId(),
            q_cookie_id: getCookie(ANALYTIC_COOKIES.Q_COOKIE_ID) ?? '',
            eventTime: Date.now(),
            quinceEventId: properties.eventId,
          })

          // Fire PageView for PDP pages where variant changes trigger new page loads
          // Check if we're on a Product Page (PDP) and not a product card or modal product display
          const pageContextDetails = getPageContextDetails()
          const isOnPDPPage = pageContextDetails.currentPage?.page_type === TRACK_PAGE_TYPE.PDP
          const isModalProductDisplay = properties.element_type === CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_DISPLAY

          if (isOnPDPPage && !isModalProductDisplay) {
            tag(EVENT_TYPE_PAGE_VIEW, {
              visitorId: getlbUserId()?.trim() ?? '',
              sessionId: getSessionId(),
              q_cookie_id: getCookie(ANALYTIC_COOKIES.Q_COOKIE_ID) ?? '',
              eventTime: Date.now(),
              quinceEventId: properties.eventId,
            })
          }
        } else {
          // Fire ViewContent event for product group (multiple variants)
          const content = {
            id: properties.spid,
            quantity: 1,
            item_price: formatRevenue(Number(properties.price)),
            variantId: '',
            sku: properties.spid,
            title: properties.productTitle,
            category: properties.department,
            brand: BRAND_QUINCE,
            type: PRODUCT_TYPE_PRODUCT_GROUP,
            image: '',
            url: getProductURL(''),
          }

          tag(EVENT_TYPE_VIEW_CONTENT, {
            currency: DEFAULT_CURRENCY_CODE,
            value: formatRevenue(Number(properties.price)),
            contents: [content],
            url: typeof window !== 'undefined' ? window.location.href : '', // Current product page pathname
            visitorId: getlbUserId()?.trim() ?? '',
            sessionId: getSessionId(),
            q_cookie_id: getCookie(ANALYTIC_COOKIES.Q_COOKIE_ID) ?? '',
            eventTime: Date.now(),
            quinceEventId: properties.eventId,
          })

          // Fire PageView for PDP pages where variant changes trigger new page loads
          // Check if we're on a Product Page (PDP) and not a product card or modal product display
          const pageContextDetails = getPageContextDetails()
          const isOnPDPPage =
            pageContextDetails.currentPage?.page_type === TRACK_PAGE_TYPE.PDP && !properties.is_product_card
          const isModalProductDisplay = properties.element_type === CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_DISPLAY

          if (isOnPDPPage && !isModalProductDisplay) {
            tag(EVENT_TYPE_PAGE_VIEW, {
              visitorId: getlbUserId()?.trim() ?? '',
              sessionId: getSessionId(),
              q_cookie_id: getCookie(ANALYTIC_COOKIES.Q_COOKIE_ID) ?? '',
              eventTime: Date.now(),
              quinceEventId: properties.eventId,
            })
          }
        }
      }
    },

    [TELEMETRY_EVENT_NAMES.EMAIL_NEWSLETTER_SUBSCRIBE]: properties => {
      const email = properties.email ?? ''
      let firstName = ''
      let lastName = ''

      // Get firstName and lastName from localStorage
      try {
        const userFromStorage = getStorage().getItem('gatsbyUser', { isGlobal: true })
        if (userFromStorage) {
          const userData = typeof userFromStorage === 'string' ? JSON.parse(userFromStorage) : userFromStorage
          firstName = userData?.given_name || userData?.name || ''
          lastName = userData?.family_name || ''
        }
      } catch (error) {
        console.error('Error getting user data from localStorage:', error)
      }

      tag(EVENT_TYPE_LEAD, {
        currency: DEFAULT_CURRENCY_CODE,
        eventTime: Date.now(),
        visitorId: getlbUserId()?.trim() ?? '',
        sessionId: getSessionId(),
        q_cookie_id: getCookie(ANALYTIC_COOKIES.Q_COOKIE_ID) ?? '',
        quinceEventId: properties.eventId,
      })

      // Set user data after tagging (without PII in the tag call)
      const trimmedEmail = email.trim()
      const trimmedFirstName = firstName.trim()
      const trimmedLastName = lastName.trim()
      const userId = getlbUserId()?.trim()

      // Only call setUserData if we have both id and email
      if (userId && trimmedEmail) {
        const userData: { id: string; email: string; firstName?: string; lastName?: string } = {
          id: userId,
          email: trimmedEmail,
        }

        if (trimmedFirstName) {
          userData.firstName = trimmedFirstName
        }
        if (trimmedLastName) {
          userData.lastName = trimmedLastName
        }

        setUserData(userData)
      }
    },

    [TELEMETRY_EVENT_NAMES.UPDATE_ITEM_QUANTITY]: properties => {
      // Fire AddToCart or RemoveFromCart based on the action
      if (properties.atcClickAction === 'QUANTITY_INCREASE') {
        // Fire AddToCart event for quantity increase
        logEdgeTagEvent(EVENT_TYPE_ADD_TO_CART, {
          productId: properties.productId,
          quantity: 1, // Always 1 for quantity increase
          price: Number(properties.price) || 0,
          variantId: properties.variantId,
          sku: properties.variantId,
          productTitle: '', // Will be handled by logEdgeTagEvent
          variantTitle: '',
          description: '',
          productType: '',
          variantImageUrl: '',
          slug: '',
          department: '',
          spid: properties.productId,
          eventId: properties.eventId,
        })
      } else if (properties.atcClickAction === 'QUANTITY_DECREASE') {
        // Fire RemoveFromCart event for quantity decrease
        logEdgeTagEvent(EVENT_TYPE_REMOVE_FROM_CART, {
          productId: properties.productId,
          quantity: 1, // Always 1 for quantity decrease
          price: Number(properties.price) || 0,
          variantId: properties.variantId,
          sku: properties.variantId,
          productTitle: '', // Will be handled by logEdgeTagEvent
          variantTitle: '',
          description: '',
          productType: '',
          variantImageUrl: '',
          slug: '',
          department: '',
          spid: properties.productId,
          eventId: properties.eventId,
        })
      }
    },

    [TELEMETRY_EVENT_NAMES.FORM_SUBMIT]: properties => {
      if (
        properties.category === 'login' &&
        (properties.action === 'account_create' || properties.action === 'account_login')
      ) {
        const email = properties.context?.login_email ?? ''
        let firstName = ''
        let lastName = ''

        // Get firstName and lastName from localStorage
        try {
          const userFromStorage = getStorage().getItem('gatsbyUser', { isGlobal: true })
          if (userFromStorage) {
            const userData = typeof userFromStorage === 'string' ? JSON.parse(userFromStorage) : userFromStorage
            firstName = userData?.given_name || userData?.name || ''
            lastName = userData?.family_name || ''
          }
        } catch (error) {
          console.error('Error getting user data from localStorage:', error)
        }

        tag(EVENT_TYPE_COMPLETE_REGISTRATION, {
          gender: (properties.context as any)?.gender ?? '',
          source:
            properties.action === 'account_create' ? (properties.context?.signup_method ?? 'general') : 'web_login',
          visitorId: getlbUserId()?.trim() ?? '',
          sessionId: getSessionId(),
          q_cookie_id: getCookie(ANALYTIC_COOKIES.Q_COOKIE_ID) ?? '',
          eventTime: Date.now(),
          quinceEventId: properties.eventId,
        })

        // Set user data after tagging (without PII in the tag call)
        const trimmedEmail = email.trim()
        const trimmedFirstName = firstName.trim()
        const trimmedLastName = lastName.trim()
        const userId = getlbUserId()?.trim()

        // Only call setUserData if we have both id and email
        if (userId && trimmedEmail) {
          const userData: { id: string; email: string; firstName?: string; lastName?: string } = {
            id: userId,
            email: trimmedEmail,
          }

          if (trimmedFirstName) {
            userData.firstName = trimmedFirstName
          }
          if (trimmedLastName) {
            userData.lastName = trimmedLastName
          }

          setUserData(userData)
        }
      }
    },
  },
}

const logEdgeTagEvent = (eventType: string, properties: EventProperties) => {
  const content = {
    id: eventType === EVENT_TYPE_REMOVE_FROM_CART ? String(properties.spid) : String(properties.productId),
    quantity: properties.quantity || 1,
    item_price: formatRevenue(properties.price),
    variantId: String(properties.variantId),
    sku: properties.sku,
    title: properties.productTitle + (properties.variantTitle ? ` - ${properties.variantTitle}` : ''),
    category: properties.department,
    brand: BRAND_QUINCE,
    type: properties.variantId ? PRODUCT_TYPE_PRODUCT : PRODUCT_TYPE_PRODUCT_GROUP,
    image: processImageUrl(properties.variantImageUrl),
    url: getProductURL(properties.slug),
  }

  // setUserProperties()

  tag(eventType, {
    currency: DEFAULT_CURRENCY_CODE,
    value: formatRevenue(properties.price * (properties.quantity || 1)),
    contents: [content],
    visitorId: getlbUserId()?.trim() ?? '',
    sessionId: getSessionId(),
    q_cookie_id: getCookie(ANALYTIC_COOKIES.Q_COOKIE_ID) ?? '',
    eventTime: Date.now(),
    quinceEventId: properties.eventId,
  })
}

export const formatRevenue = (price: number | null) => {
  return price ?? 0
}

export const getProductURL = (slug: string) => QUINCE_DOMAIN + '/' + slug

// TODO: Add this back in when we need to make the user data call
// const setUserProperties = () => {
//   const userEmail = getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL)

//   userEmail && setUserData({ id: getlbUserId()?.trim() ?? '', email: userEmail })
// }
